import { useState } from 'react';
import { Box, Checkbox, IconButton, Radio, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { PopUpModal } from '../../components/molecules/PopUpModal/PopUpModal';
import { BasicButton, BasicDatePicker, TermChip } from '../../components/atoms';
import { ActionRequiredGoal } from '../../features/advisorConsole/types';
import { formatCurrency } from '../../utils';
import { useIsMultiPortfolioTenant } from '../../common/hooks';
import { REVIEW_GOAL_EVENT_TYPES } from '../../common/constants';
import { getGoalTenureWindow } from '../goals-inline-flow2/goalInlineHelpers';
import { ReviewGoalsResult } from './applyReviewDecisions';
import { ReactComponent as InfoCircleFilledIcon } from '../../assets/icons/info-goals-page.svg';
import { ReactComponent as TrashIcon } from '../../assets/icons/trash.svg';
import { ReactComponent as SuccessTickIcon } from '../../assets/icons/success_tick.svg';
import { ReactComponent as CheckIcon } from '../../assets/icons/check.svg';

dayjs.extend(customParseFormat);

// AEX-3218: "Approaching Maturity" status pill tokens are not on the theme palette yet (design
// "Core/Tertiary/tc2"); mirror the TermChip precedent of inlining them until they are added.
const APPROACHING_BORDER = '#FFCE00';
const APPROACHING_BG = '#FFFCF0';
const APPROACHING_TEXT = '#997A00';

const COLUMN_WIDTHS = { name: 240, status: 160, amount: 168, date: 160, actions: 246 };
// Shared grid tracks (fr = Figma proportions) so the header and every row use identical column widths.
const GRID_TEMPLATE_COLUMNS = `${COLUMN_WIDTHS.name}fr ${COLUMN_WIDTHS.status}fr ${COLUMN_WIDTHS.amount}fr ${COLUMN_WIDTHS.date}fr ${COLUMN_WIDTHS.actions}fr`;
const APPROACHING_EVENT_TYPES = ['GOAL_NEAR_MATURITY'];

type GoalKind = 'matured' | 'approaching';
type MaturedAction = 'realized' | 'notRealized';

type GoalDecision = {
    action?: MaturedAction;
    acknowledged?: boolean;
    newTargetDate?: Dayjs | null;
};

type ReviewGoalsModalProps = {
    isOpen: boolean;
    onClose: () => void;
    // eslint-disable-next-line no-unused-vars
    onNext: (result: ReviewGoalsResult) => void;
    goals: ActionRequiredGoal[];
};

const getGoalKind = (goal: ActionRequiredGoal): GoalKind => {
    if (goal.eventType) {
        return APPROACHING_EVENT_TYPES.includes(goal.eventType) ? 'approaching' : 'matured';
    }
    return (goal.severity ?? '').toUpperCase() === 'WARNING' ? 'approaching' : 'matured';
};

// BE sends the goal target date as DD-MM-YYYY; fall back to a flexible parse for safety.
const parseTargetDate = (targetDate?: string): Dayjs | null => {
    if (!targetDate) return null;
    const strict = dayjs(targetDate, ['DD-MM-YYYY', 'YYYY-MM-DD', 'MM-DD-YYYY'], true);
    if (strict.isValid()) return strict;
    const loose = dayjs(targetDate);
    return loose.isValid() ? loose : null;
};

// Allowed new target-date window for a not-realized goal: the goal's current tenure band measured from
// today (so the Short/Medium/Long category is preserved), clamped to the future (>= next month).
const getNotRealizedDateWindow = (goalCategory?: string): { min: Dayjs; max?: Dayjs } => {
    const nextMonth = dayjs().add(1, 'month').startOf('month');
    const window = getGoalTenureWindow(goalCategory, dayjs());
    if (!window) return { min: nextMonth };
    const min = window.minDate.isBefore(nextMonth, 'month') ? nextMonth : window.minDate;
    return { min, max: window.maxDate };
};

const isDateInWindow = (date: Dayjs | null | undefined, window: { min: Dayjs; max?: Dayjs }): boolean => {
    if (!date) return false;
    const month = date.startOf('month');
    if (month.isBefore(window.min, 'month')) return false;
    if (window.max && month.isAfter(window.max, 'month')) return false;
    return true;
};

// A goal counts as "addressed" (reviewed) once the advisor has made a complete decision for it.
const isGoalAddressed = (kind: GoalKind, decision: GoalDecision | undefined, goal: ActionRequiredGoal): boolean => {
    if (kind === 'approaching') return !!decision?.acknowledged;
    if (decision?.action === 'realized') return !!decision.acknowledged;
    if (decision?.action === 'notRealized')
        return isDateInWindow(decision?.newTargetDate, getNotRealizedDateWindow(goal.goalCategory));
    return false;
};

const ReviewGoalRow = ({
    goal,
    decision,
    onChange
}: {
    goal: ActionRequiredGoal;
    decision: GoalDecision;
    // eslint-disable-next-line no-unused-vars
    onChange: (patch: GoalDecision) => void;
}) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const isMultiPortfolioTenant = useIsMultiPortfolioTenant();
    const kind = getGoalKind(goal);
    const isMatured = kind === 'matured';

    const isRemoved = isMatured && decision.action === 'realized' && !!decision.acknowledged;
    const isAcknowledgedApproaching = !isMatured && !!decision.acknowledged;
    const isEditingDate = isMatured && decision.action === 'notRealized';
    const dateWindow = getNotRealizedDateWindow(goal.goalCategory);
    const hasPastDateError =
        isEditingDate && !!decision.newTargetDate && !isDateInWindow(decision.newTargetDate, dateWindow);

    const accentColor = isRemoved
        ? theme.palette.semantic.bd.warning
        : isAcknowledgedApproaching
        ? theme.palette.semantic.bd.success
        : undefined;

    const cellSx = (width?: number, withLeftBorder = true) => ({
        // Width comes from the parent grid track (GRID_TEMPLATE_COLUMNS); minWidth:0 keeps the fr tracks
        // at the Figma proportions even when a cell's content is wide.
        minWidth: width ? 0 : '48px',
        padding: '12px 8px',
        borderLeft: withLeftBorder ? `1px solid ${theme.palette.semantic.bd.default}` : 'none',
        display: 'flex',
        flexDirection: 'column' as const,
        justifyContent: 'flex-start' as const,
        gap: '4px'
    });

    const baseText = {
        fontFamily: '"TT Commons Pro"',
        fontSize: '16px',
        lineHeight: '22px',
        color: theme.palette.semantic.text.default
    };
    const captionText = { fontFamily: '"TT Commons Pro"', fontSize: '12px', lineHeight: '17px' };

    const renderRadioIcon = (checked: boolean) => (
        <Box
            component="span"
            sx={{
                boxSizing: 'border-box',
                width: '18px',
                height: '18px',
                borderRadius: '50%',
                border: `2px solid ${theme.palette.semantic.int.radioButton}`,
                backgroundColor: theme.palette.semantic.bg.default,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
        >
            {checked && (
                <Box
                    component="span"
                    sx={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: theme.palette.semantic.int.radioButton
                    }}
                />
            )}
        </Box>
    );

    const renderCheckboxIcon = (checked: boolean) => (
        <Box
            component="span"
            sx={{
                boxSizing: 'border-box',
                width: '16px',
                height: '16px',
                borderRadius: '2px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                backgroundColor: checked ? theme.palette.semantic.int.checkBox : theme.palette.semantic.bg.default,
                border: checked ? 'none' : `1px solid ${theme.palette.semantic.icon.subtle}`,
                '& svg': { width: '16px', height: '16px', flexShrink: 0 },
                '& svg path': { fill: theme.palette.semantic.icon.inverse }
            }}
        >
            {checked && <CheckIcon aria-hidden />}
        </Box>
    );

    const renderDaysLabel = () => {
        const targetDate = parseTargetDate(goal.targetDate);
        if (!targetDate) return null;
        const diff = targetDate.startOf('day').diff(dayjs().startOf('day'), 'day');
        let label: string;
        let color: string = theme.palette.semantic.text.error;
        if (diff > 0) {
            label = t(diff === 1 ? 'REVIEW_GOALS_DAY_LEFT' : 'REVIEW_GOALS_DAYS_LEFT', { count: diff });
            if (!isMatured) color = APPROACHING_TEXT;
        } else if (diff === 0) {
            label = t('REVIEW_GOALS_DUE_TODAY');
        } else {
            const passed = Math.abs(diff);
            label = t(passed === 1 ? 'REVIEW_GOALS_DAY_PASSED' : 'REVIEW_GOALS_DAYS_PASSED', { count: passed });
        }
        return (
            <Box component="span" sx={{ ...captionText, color }}>
                {label}
            </Box>
        );
    };

    const renderStatusPill = () => (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '28px',
                padding: '4px 12px',
                borderRadius: '100px',
                border: '1px solid',
                borderColor: isMatured ? theme.palette.semantic.bd.warning : APPROACHING_BORDER,
                backgroundColor: isMatured ? theme.palette.semantic.bg.warning1 : APPROACHING_BG,
                color: theme.palette.semantic.text.default,
                whiteSpace: 'nowrap',
                ...captionText
            }}
        >
            {t(isMatured ? 'REVIEW_GOALS_STATUS_GOAL_MATURED' : 'REVIEW_GOALS_STATUS_APPROACHING')}
        </Box>
    );

    const renderActions = () => {
        if (!isMatured) {
            return (
                <Box
                    component="label"
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '6px',
                        width: '100%',
                        padding: '2px 0 4px 8px',
                        cursor: 'pointer'
                    }}
                >
                    <Checkbox
                        disableRipple
                        icon={renderCheckboxIcon(false)}
                        checkedIcon={renderCheckboxIcon(true)}
                        sx={{
                            padding: '2px 0 0 0',
                            flexShrink: 0,
                            '&.Mui-focusVisible > span': {
                                outline: `2px solid ${theme.palette.semantic.int.focused}`,
                                outlineOffset: '2px'
                            }
                        }}
                        checked={!!decision.acknowledged}
                        onChange={(e) => onChange({ acknowledged: e.target.checked })}
                    />
                    <Box
                        component="span"
                        sx={{
                            ...baseText,
                            fontWeight: 400,
                            fontSize: '14px',
                            lineHeight: '19px',
                            fontFeatureSettings: '"liga" 0',
                            flex: '1 0 0',
                            minWidth: 0,
                            wordBreak: 'break-word'
                        }}
                    >
                        {t('REVIEW_GOALS_ACKNOWLEDGE')}
                    </Box>
                </Box>
            );
        }
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', paddingRight: '8px' }}>
                    {(['realized', 'notRealized'] as MaturedAction[]).map((action) => (
                        <Box
                            key={action}
                            component="label"
                            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                        >
                            <Radio
                                icon={renderRadioIcon(false)}
                                checkedIcon={renderRadioIcon(true)}
                                sx={{ padding: '7px' }}
                                checked={decision.action === action}
                                onChange={() =>
                                    onChange(
                                        action === 'realized'
                                            ? { action, acknowledged: false, newTargetDate: null }
                                            : { action, acknowledged: false }
                                    )
                                }
                            />
                            <Box component="span" sx={{ ...baseText, fontSize: '14px', lineHeight: '19px' }}>
                                {t(action === 'realized' ? 'REVIEW_GOALS_REALIZED' : 'REVIEW_GOALS_NOT_REALIZED')}
                            </Box>
                        </Box>
                    ))}
                </Box>
                {decision.action === 'realized' && (
                    <Box
                        component="label"
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '6px',
                            width: '100%',
                            padding: '2px 0 4px 8px',
                            cursor: 'pointer'
                        }}
                    >
                        <Checkbox
                            disableRipple
                            icon={renderCheckboxIcon(false)}
                            checkedIcon={renderCheckboxIcon(true)}
                            sx={{
                                padding: '2px 0 0 0',
                                flexShrink: 0,
                                '&.Mui-focusVisible > span': {
                                    outline: `2px solid ${theme.palette.semantic.int.focused}`,
                                    outlineOffset: '2px'
                                }
                            }}
                            checked={!!decision.acknowledged}
                            onChange={(e) => onChange({ acknowledged: e.target.checked })}
                        />
                        <Box
                            component="span"
                            sx={{
                                ...baseText,
                                fontWeight: 400,
                                fontSize: '14px',
                                lineHeight: '19px',
                                flex: '1 0 0',
                                minWidth: 0,
                                wordBreak: 'break-word'
                            }}
                        >
                            {t('REVIEW_GOALS_REALIZED_ACK')}
                        </Box>
                    </Box>
                )}
                {decision.action === 'notRealized' && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '8px',
                            padding: '4px',
                            borderRadius: '4px',
                            backgroundColor: theme.palette.semantic.bg.info,
                            '& svg': { width: '16px', height: '16px', flexShrink: 0 },
                            '& svg path': { fill: theme.palette.semantic.icon.info }
                        }}
                    >
                        <InfoCircleFilledIcon aria-hidden />
                        <Box
                            component="span"
                            sx={{
                                ...baseText,
                                fontSize: '14px',
                                lineHeight: '19px',
                                color: theme.palette.semantic.text.default
                            }}
                        >
                            {t('REVIEW_GOALS_SELECT_FUTURE_DATE_INFO')}
                        </Box>
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: GRID_TEMPLATE_COLUMNS,
                alignItems: 'stretch',
                borderBottom: `1px solid ${theme.palette.semantic.bd.default}`,
                backgroundColor: theme.palette.semantic.bg.default
            }}
        >
            {/* Goal Name */}
            <Box
                sx={{
                    ...cellSx(COLUMN_WIDTHS.name, false),
                    gap: '8px',
                    padding: '12px 16px',
                    borderLeft: accentColor ? `4px solid ${accentColor}` : 'none'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {isRemoved && (
                        <Box
                            aria-hidden
                            sx={{
                                flexShrink: 0,
                                width: '16px',
                                height: '16px',
                                borderRadius: '100px',
                                backgroundColor: theme.palette.semantic.bd.warning,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                '& svg': { width: '12px', height: '12px' },
                                '& path': { fill: theme.palette.semantic.text.inverse }
                            }}
                        >
                            <TrashIcon />
                        </Box>
                    )}
                    {isAcknowledgedApproaching && (
                        <SuccessTickIcon width={16} height={16} style={{ flexShrink: 0 }} aria-hidden />
                    )}
                    <Box component="span" sx={{ ...baseText }}>
                        {goal.goalName}
                    </Box>
                </Box>
                {isMultiPortfolioTenant && goal.goalCategory && (
                    <Box>
                        <TermChip term={goal.goalCategory} variant="bordered" />
                    </Box>
                )}
            </Box>
            {/* Status */}
            <Box sx={{ ...cellSx(COLUMN_WIDTHS.status), justifyContent: 'flex-start', alignItems: 'flex-start' }}>
                {renderStatusPill()}
            </Box>
            {/* Target Amount */}
            <Box sx={{ ...cellSx(COLUMN_WIDTHS.amount), alignItems: 'flex-end' }}>
                <Box component="span" sx={{ ...baseText }}>
                    {goal.targetAmount != null ? formatCurrency(goal.targetAmount) : ''}
                </Box>
            </Box>
            {/* Goal Date */}
            <Box sx={{ ...cellSx(COLUMN_WIDTHS.date) }}>
                {isEditingDate ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px',
                            width: '100%',
                            '& .MuiOutlinedInput-root': {
                                height: '32px',
                                padding: '0 36px 0 8px',
                                borderRadius: '4px',
                                overflow: 'hidden',
                                backgroundColor: theme.palette.semantic.bg.default,
                                '&:not(.Mui-error), &:not(.Mui-error):hover': {
                                    borderColor: theme.palette.neutral[500]
                                }
                            },
                            '& .MuiOutlinedInput-input': {
                                ...baseText,
                                fontWeight: 400,
                                height: '22px',
                                padding: 0
                            },
                            '& .MuiInputAdornment-root': { marginLeft: 0 },
                            '& .MuiOutlinedInput-root .MuiIconButton-root': {
                                width: '36px',
                                padding: '0 8px',
                                borderRadius: 0
                            }
                        }}
                    >
                        <BasicDatePicker
                            isMmYyyy
                            enabled
                            customOpenIcon
                            startDate={dateWindow.min.format('YYYY-MM-DD')}
                            endDate={dateWindow.max ? dateWindow.max.format('YYYY-MM-DD') : undefined}
                            value={decision.newTargetDate ?? null}
                            hasError={hasPastDateError}
                            onChange={(value: Dayjs | null) => onChange({ newTargetDate: value })}
                        />
                        {hasPastDateError && (
                            <Box component="span" sx={{ ...captionText, color: theme.palette.semantic.text.error }}>
                                {t('REVIEW_GOALS_SELECT_FUTURE_DATE_ERROR')}
                            </Box>
                        )}
                    </Box>
                ) : (
                    <>
                        <Box component="span" sx={{ ...baseText }}>
                            {(() => {
                                const parsed = parseTargetDate(goal.targetDate);
                                return parsed ? parsed.format('DD MMM YYYY') : '';
                            })()}
                        </Box>
                        {renderDaysLabel()}
                    </>
                )}
            </Box>
            {/* Actions */}
            <Box sx={{ ...cellSx(COLUMN_WIDTHS.actions) }}>{renderActions()}</Box>
        </Box>
    );
};

export const ReviewGoalsModal = ({ isOpen, onClose, onNext, goals }: ReviewGoalsModalProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [decisions, setDecisions] = useState<Record<string, GoalDecision>>({});

    const total = goals.length;
    const reviewedCount = goals.filter((goal) =>
        isGoalAddressed(getGoalKind(goal), decisions[goal.goalId], goal)
    ).length;
    const allAddressed = total > 0 && reviewedCount === total;

    const updateDecision = (goalId: string, patch: GoalDecision) =>
        setDecisions((prev) => ({ ...prev, [goalId]: { ...prev[goalId], ...patch } }));

    // AEX-3218: translate the advisor's decisions into realized-goal deletions + not-realized date updates.
    const buildResult = (): ReviewGoalsResult => {
        const realizedGoalIds: string[] = [];
        const notRealizedUpdates: { goalId: string; newTargetDate: string }[] = [];
        const notificationUpdates: { notification_id?: string; status?: string }[] = [];
        const eventUpdates: { event_type: string; goal_id: string }[] = [];
        goals.forEach((goal) => {
            const decision = decisions[goal.goalId];
            if (Object.keys(decision ?? {}).length) {
                notificationUpdates.push({
                    notification_id: goal.notification_id,
                    status: 'ACK'
                });
            }
            if (getGoalKind(goal) !== 'matured') return;
            if (decision?.action === 'realized' && decision.acknowledged) {
                realizedGoalIds.push(goal.goalId);
                eventUpdates.push({
                    event_type: REVIEW_GOAL_EVENT_TYPES.realized,
                    goal_id: goal.goalId
                });
            } else if (decision?.action === 'notRealized' && decision.newTargetDate) {
                notRealizedUpdates.push({
                    goalId: goal.goalId,
                    newTargetDate: decision.newTargetDate.startOf('month').format('DD-MM-YYYY')
                });
                eventUpdates.push({
                    event_type: REVIEW_GOAL_EVENT_TYPES.notRealized,
                    goal_id: goal.goalId
                });
            }
        });
        return { realizedGoalIds, notRealizedUpdates, notificationUpdates, eventUpdates };
    };

    const headerCellSx = (width?: number, align: 'flex-start' | 'center' | 'flex-end' = 'flex-start') => ({
        minWidth: width ? 0 : '48px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: align,
        padding: '8px',
        borderLeft: `1px solid ${theme.palette.semantic.bd.default}`,
        fontFamily: '"TT Commons Pro"',
        fontWeight: 700,
        fontSize: '14px',
        lineHeight: '19px',
        color: theme.palette.semantic.text.default
    });

    return (
        <PopUpModal
            open={isOpen}
            width="1300px"
            height="85vh"
            transitionDuration={0}
            title={t('REVIEW_GOALS_STEP1_TITLE')}
            showTitle={false}
            showCancelButton={false}
            handleClose={onClose}
            handleSaveButtonAction={() => onNext(buildResult())}
            sx={{
                '& .MuiDialogContent-root': { padding: '32px', borderTop: 'none', borderBottom: 'none' },
                '& .MuiDialogActions-root': { padding: '0 32px 32px' }
            }}
            description={
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '24px'
                    }}
                >
                    {/* Header */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                            <Box
                                component="span"
                                sx={{
                                    flex: '1 1 0',
                                    fontFamily: '"TT Commons Pro"',
                                    fontWeight: 700,
                                    fontSize: '24px',
                                    lineHeight: '30px',
                                    color: theme.palette.primary[800]
                                }}
                            >
                                {t('REVIEW_GOALS_STEP1_TITLE')}
                            </Box>
                            <IconButton
                                aria-label="Close"
                                onClick={onClose}
                                sx={{ padding: '4px', color: theme.palette.semantic.icon.dark }}
                            >
                                <CloseIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        </Box>
                        <Box
                            component="span"
                            sx={{
                                fontFamily: '"TT Commons Pro"',
                                fontWeight: 400,
                                fontSize: '16px',
                                lineHeight: '22px',
                                color: theme.palette.semantic.text.default
                            }}
                        >
                            {t('REVIEW_GOALS_STEP1_SUBTITLE')}
                        </Box>
                    </Box>
                    {/* Progress */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', width: '100%' }}>
                        <Box
                            sx={{
                                flex: '1 1 0',
                                height: '12px',
                                borderRadius: '16px',
                                backgroundColor: theme.palette.semantic.bg.disabled,
                                overflow: 'hidden'
                            }}
                        >
                            <Box
                                sx={{
                                    height: '100%',
                                    width: `${total ? (reviewedCount / total) * 100 : 0}%`,
                                    backgroundColor: theme.palette.semantic.icon.success,
                                    borderRadius: '16px',
                                    transition: 'width 0.2s ease'
                                }}
                            />
                        </Box>
                        <Box
                            component="span"
                            sx={{
                                whiteSpace: 'nowrap',
                                fontFamily: '"TT Commons Pro"',
                                fontSize: '16px',
                                lineHeight: '22px',
                                color: theme.palette.semantic.text.muted
                            }}
                        >
                            {total === 1
                                ? t('REVIEW_GOALS_PROGRESS_ONE', { reviewed: reviewedCount })
                                : t('REVIEW_GOALS_PROGRESS', { reviewed: reviewedCount, total })}
                        </Box>
                    </Box>
                    {/* Table */}
                    <Box
                        sx={{
                            width: '100%',
                            border: `1px solid ${theme.palette.semantic.bd.default}`,
                            borderRadius: '8px',
                            overflow: 'hidden'
                        }}
                    >
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: GRID_TEMPLATE_COLUMNS,
                                backgroundColor: theme.palette.semantic.bg.raised,
                                borderBottom: `1px solid ${theme.palette.semantic.bd.default}`
                            }}
                        >
                            <Box sx={{ ...headerCellSx(COLUMN_WIDTHS.name), borderLeft: 'none' }}>
                                {t('REVIEW_GOALS_COL_GOAL_NAME')}
                            </Box>
                            <Box sx={{ ...headerCellSx(COLUMN_WIDTHS.status, 'flex-start') }}>
                                {t('REVIEW_GOALS_COL_STATUS')}
                            </Box>
                            <Box sx={{ ...headerCellSx(COLUMN_WIDTHS.amount, 'flex-end') }}>
                                {t('REVIEW_GOALS_COL_TARGET_AMOUNT')}
                            </Box>
                            <Box sx={{ ...headerCellSx(COLUMN_WIDTHS.date, 'flex-start') }}>
                                {t('REVIEW_GOALS_COL_GOAL_DATE')}
                            </Box>
                            <Box sx={{ ...headerCellSx(COLUMN_WIDTHS.actions, 'flex-start') }}>
                                {t('REVIEW_GOALS_COL_ACTIONS')}
                            </Box>
                        </Box>
                        {goals.map((goal) => (
                            <ReviewGoalRow
                                key={goal.goalId}
                                goal={goal}
                                decision={decisions[goal.goalId] ?? {}}
                                onChange={(patch) => updateDecision(goal.goalId, patch)}
                            />
                        ))}
                    </Box>
                </Box>
            }
            footerComp={
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        gap: '16px'
                    }}
                >
                    <Box sx={{ flex: '1 1 0' }}>
                        <Box
                            component="button"
                            type="button"
                            onClick={onClose}
                            sx={{
                                background: 'transparent',
                                border: 'none',
                                padding: 0,
                                cursor: 'pointer',
                                fontFamily: '"TT Commons Pro"',
                                fontWeight: 400,
                                fontSize: '16px',
                                lineHeight: '22px',
                                fontFeatureSettings: '"liga" 0',
                                whiteSpace: 'nowrap',
                                color: theme.palette.semantic.int.selected,
                                textDecorationLine: 'underline',
                                textDecorationStyle: 'solid',
                                textDecorationThickness: 'from-font',
                                textDecorationSkipInk: 'none',
                                textUnderlinePosition: 'from-font'
                            }}
                        >
                            {t('BUTTON_CANCEL')}
                        </Box>
                    </Box>
                    {!allAddressed && (
                        <Box
                            component="span"
                            sx={{
                                fontFamily: '"TT Commons Pro"',
                                fontSize: '16px',
                                lineHeight: '22px',
                                color: theme.palette.semantic.text.mutedLight
                            }}
                        >
                            {total === 1
                                ? t('REVIEW_GOALS_ADDRESS_ALL_ONE')
                                : t('REVIEW_GOALS_ADDRESS_ALL', { count: total })}
                        </Box>
                    )}
                    <BasicButton
                        variant="contained"
                        disabled={!allAddressed}
                        onClick={() => onNext(buildResult())}
                        sx={{ fontWeight: 400 }}
                    >
                        {t('REVIEW_GOALS_NEXT')}
                    </BasicButton>
                </Box>
            }
        />
    );
};

export default ReviewGoalsModal;
